import ExcelJS, { Borders } from 'exceljs'

interface StudentAttendanceRecord {
  student_name: string
  reg_id: string
  class: string
  division: string
  date: string
  is_present: '1' | '2'
  daily_bus_trip_type: 'pickup' | 'drop'
  driver_name: string
  driver_phone: string
  conductor_name: string
  conductor_mobile: string
  trip_number: string
  parent_mobile: string
  route_name: string
}

const thinBorder = {
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' },
} as Partial<Borders>

const styles = {
  heading: {
    height: 32,
    font: { size: 14, bold: true },
    border: {
      top: { style: 'thick' },
      left: { style: 'thick' },
      bottom: { style: 'thick' },
      right: { style: 'thick' },
    } as Partial<Borders>,
    alignment: { horizontal: 'center', vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
  detailHeader: {
    font: { size: 14, bold: true, color: { argb: 'FFFF0000' } },
    border: thinBorder,
  },
  baseHeader: {
    font: { bold: true, size: 12 },
    border: thinBorder,
    alignment: { horizontal: 'center', vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
  mergedHeader: {
    font: { bold: true, size: 12 },
    border: thinBorder,
    alignment: { horizontal: 'center', vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
  mHeader: {
    font: { bold: true, size: 14, color: { argb: 'FF548DD4' } },
    border: thinBorder,
    alignment: { horizontal: 'center', vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
  aHeader: {
    font: { bold: true, size: 14, color: { argb: 'FFFF0000' } },
    border: thinBorder,
    alignment: { horizontal: 'center', vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
  totalAttendanceHeader: {
    font: { bold: true, size: 12 },
    border: thinBorder,
    alignment: {
      wrapText: true,
      vertical: 'middle',
      horizontal: 'center',
    } as Partial<ExcelJS.Alignment>,
  },
  totalAttendanceCount: {
    font: { size: 12 },
  },
  presentStatus: {
    font: { size: 12 },
    presentColor: { argb: 'FF7766BD' },
    absentColor: { argb: 'FFFF0000' },
    border: thinBorder,
    alignment: {
      vertical: 'middle',
      horizontal: 'center',
    } as Partial<ExcelJS.Alignment>,
  },
  studentData: {
    font: { size: 14 },
    border: thinBorder,
    alignment: { vertical: 'middle' } as Partial<ExcelJS.Alignment>,
  },
}

export const generateStudentAttendanceWorksheet = (
  workbook: ExcelJS.Workbook,
  data: any[],
  dates: number[],
  heading: string
) => {
  const worksheet = workbook.addWorksheet('Sheet1')

  const baseHeaders = ['SI/NO.', 'ADMIS:NO.', 'STUDENT NAME', 'CLASS', 'Divi:', 'Mobile No.']
  const startRow = 1

  // Heading
  worksheet.getRow(startRow).height = 32
  worksheet.mergeCells(startRow, 1, startRow, baseHeaders.length + dates.length * 2 + 1)
  const headingCell = worksheet.getCell(startRow, 1)
  headingCell.value = heading
  headingCell.font = styles.heading.font
  headingCell.style.border = styles.heading.border
  headingCell.alignment = styles.heading.alignment

  worksheet.mergeCells(2, 1, 2, baseHeaders.length + dates.length * 2 + 1)

  // Trip details
  const tripMeta = data[0]
  const metaRow = 3
  worksheet.mergeCells(metaRow, 1, metaRow, 2)
  worksheet.getCell(metaRow, 1).value = `BUS NO: ${tripMeta.trip_number}`
  worksheet.getCell(metaRow, 1).style.font = styles.detailHeader.font

  worksheet.mergeCells(metaRow, 3, metaRow, 5)
  worksheet.getCell(metaRow, 3).value = `ROUTE: - ${tripMeta.route_name}`
  worksheet.getCell(metaRow, 3).style.font = styles.detailHeader.font

  worksheet.mergeCells(metaRow, 6, metaRow, 11)
  worksheet.getCell(metaRow, 6).value = `DRIVER: ${tripMeta.driver_name} - ${tripMeta.driver_phone}`
  worksheet.getCell(metaRow, 6).style.font = styles.detailHeader.font

  worksheet.mergeCells(metaRow, 12, metaRow, 18)
  worksheet.getCell(metaRow, 12).value = `CONDUCTOR: ${tripMeta.conductor_name} - ${tripMeta.conductor_mobile}`
  worksheet.getCell(metaRow, 12).style.font = styles.detailHeader.font

  const headerRow1 = 4
  const headerRow2 = 5
  const attendanceColOffset = baseHeaders.length

  // Base headers
  baseHeaders.forEach((title, index) => {
    worksheet.mergeCells(headerRow1, index + 1, headerRow2, index + 1)
    const cell = worksheet.getCell(headerRow1, index + 1)
    cell.value = title
    cell.font = styles.baseHeader.font
    cell.alignment = styles.baseHeader.alignment
  })

  worksheet.getRow(headerRow1).height = 28
  worksheet.getRow(headerRow2).height = 28

  // Determine month/year from records
  const firstRecord = data[0]
  const recordMonth = new Date(firstRecord.date).getMonth()
  const recordYear = new Date(firstRecord.date).getFullYear()

  // Dates header
  dates.forEach((dayNumber, dIndex) => {
    const baseCol = attendanceColOffset + dIndex * 2 + 1
    const dateObj = new Date(recordYear, recordMonth, dayNumber)
    const formattedDate = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })

    worksheet.mergeCells(headerRow1, baseCol, headerRow1, baseCol + 1)
    const mergedHeader = worksheet.getCell(headerRow1, baseCol)
    mergedHeader.value = formattedDate
    mergedHeader.alignment = styles.mergedHeader.alignment
    mergedHeader.font = styles.mergedHeader.font

    const mCell = worksheet.getCell(headerRow2, baseCol)
    mCell.value = 'M'
    mCell.style.font = styles.mHeader.font
    mCell.alignment = styles.mHeader.alignment

    const aCell = worksheet.getCell(headerRow2, baseCol + 1)
    aCell.value = 'A'
    aCell.style.font = styles.aHeader.font
    aCell.alignment = styles.aHeader.alignment
  })

  const totalCol = attendanceColOffset + dates.length * 2 + 1
  worksheet.mergeCells(headerRow1, totalCol, headerRow2, totalCol)
  const totalHeader = worksheet.getCell(headerRow1, totalCol)
  totalHeader.value = 'No. of \n days \n attended'
  totalHeader.font = styles.totalAttendanceHeader.font
  totalHeader.alignment = styles.totalAttendanceHeader.alignment

  // Group by student
  const studentMap = new Map<string, StudentAttendanceRecord[]>()
  data.forEach((record) => {
    const key = `${record.reg_id}-${record.student_name}`
    if (!studentMap.has(key)) studentMap.set(key, [])
    studentMap.get(key)!.push(record)
  })

  let studentRowIndex = headerRow2 + 1

  // === Populate student rows ===
  Array.from(studentMap.entries()).forEach(([key, records], i) => {
    const [reg_id, name] = key.split('-')
    const studentRow = worksheet.getRow(studentRowIndex)
    const sample = records[0]

    studentRow.getCell(1).value = i + 1
    studentRow.getCell(2).value = reg_id
    studentRow.getCell(3).value = name
    studentRow.getCell(4).value = sample.class
    studentRow.getCell(5).value = sample.division
    studentRow.getCell(6).value = sample.parent_mobile

    let attendanceCount = 0

    dates.forEach((dayNumber, dIndex) => {
      const baseCol = attendanceColOffset + dIndex * 2 + 1
      const pickup = records.find(
        (r) => new Date(r.date).getDate() === dayNumber && r.daily_bus_trip_type === 'pickup'
      )
      const drop = records.find(
        (r) => new Date(r.date).getDate() === dayNumber && r.daily_bus_trip_type === 'drop'
      )

      // ✅ Updated logic: mark ✔️ if present, ❌ if record but not present, blank if no record
      const getStatusEmoji = (shift: any) => {
        if (!shift) return '❌' // no record = empty
        return shift.is_present === '1' ? '✔️' : '❌'
      }

      const pickupVal = getStatusEmoji(pickup)
      const dropVal = getStatusEmoji(drop)

      const pickupColor =
        pickupVal === '✔️' ? styles.presentStatus.presentColor : styles.presentStatus.absentColor
      const dropColor =
        dropVal === '✔️' ? styles.presentStatus.presentColor : styles.presentStatus.absentColor

      if (pickupVal === '✔️') attendanceCount++
      if (dropVal === '✔️') attendanceCount++

      // Always set cell (either ✔️, ❌, or blank)
      studentRow.getCell(baseCol).value = pickupVal
      studentRow.getCell(baseCol).style.alignment = styles.presentStatus.alignment
      studentRow.getCell(baseCol).style.font = {
        ...styles.presentStatus.font,
        color: pickupVal ? pickupColor : undefined,
      }

      studentRow.getCell(baseCol + 1).value = dropVal
      studentRow.getCell(baseCol + 1).style.alignment = styles.presentStatus.alignment
      studentRow.getCell(baseCol + 1).style.font = {
        ...styles.presentStatus.font,
        color: dropVal ? dropColor : undefined,
      }
    })

    studentRow.getCell(totalCol).value = attendanceCount
    studentRow.getCell(totalCol).style.font = styles.totalAttendanceCount.font
    studentRowIndex++
  })

  // Signature Section
  let signatureIndex = studentRowIndex + 2
  const addSignRow = (label: string) => {
    worksheet.mergeCells(signatureIndex, 1, signatureIndex, 2)
    worksheet.getRow(signatureIndex).getCell(3).value = label
    worksheet.getRow(signatureIndex).getCell(3).style.font = { size: 18, bold: true }
    worksheet.getRow(signatureIndex).getCell(3).style.border = thinBorder
    signatureIndex++
  }

  addSignRow('Total Attendance')
  addSignRow('Conductor Signature')
  addSignRow("Driver's Signature")
  addSignRow("Teacher In-charge's Signature")
  addSignRow('Verified by Zone In-charge')

  return worksheet
}
